// Platform console → 📨 Enquiries (docs/CLINTOBYTES_WEBSITE_DESIGN.md §B8.2).
// The website's "Talk to us" form lands here. Each enquiry has a status, an
// owner and a running list of notes (nothing is overwritten), and can be turned
// into a hospital with the Provisioning Wizard, or linked to an existing one.
import { useEffect, useState } from 'react';
import { platformApi, platformAuthHeader } from '../lib/platformApi/client';

export interface EnquirySummary {
  id: number; ref: string; name: string | null; organisation: string | null; email: string | null; phone: string | null;
  city: string | null; state: string | null; segment: string | null; doctors: number | null; beds: number | null;
  interest: string; plan_key: string | null; status: Status; owner_operator_id: number | null; owner_name: string | null;
  tenant_id: number | null; tenant_slug: string | null; created_at: string; purged_at: string | null;
}
interface EnquiryEvent { id: number; kind: string; body: string | null; before: Record<string, unknown> | null; after: Record<string, unknown> | null; created_at: string; operator_name: string | null }
interface EnquiryDetail extends EnquirySummary {
  role: string | null; message: string | null; source_page: string | null; utm: Record<string, string>;
  consent_text_version: string; consent_at: string; tenant_name: string | null; events: EnquiryEvent[];
}
type Status = 'new' | 'contacted' | 'demo_booked' | 'won' | 'lost' | 'spam';
interface Operator { id: number; username: string; full_name: string | null }
interface TenantRow { id: number; slug: string; name: string; status: string }

const STATUS_LABEL: Record<Status, string> = {
  new: 'New', contacted: 'Contacted', demo_booked: 'Demo booked', won: 'Won', lost: 'Lost', spam: 'Spam',
};
const STATUS_COLOUR: Record<Status, [string, string]> = {
  new: ['#DBEAFE', '#1E40AF'], contacted: ['#FEF3C7', '#92400E'], demo_booked: ['#EDE9FE', '#5B21B6'],
  won: ['#DCFCE7', '#166534'], lost: ['#F3F4F6', '#4B5563'], spam: ['#FEE2E2', '#991B1B'],
};
const SEGMENT_LABEL: Record<string, string> = {
  clinic: 'Clinic', polyclinic: 'Polyclinic', small_hospital: 'Small hospital', multispecialty: 'Multispecialty', other: 'Other',
};
const INTEREST_LABEL: Record<string, string> = { demo: 'Demo', plan: 'A plan', enterprise: 'Enterprise / quote', other: 'Other' };

const btn = { background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' } as const;
const primary = { ...btn, background: '#0B5FD6', color: '#fff', border: '1px solid #0B5FD6' } as const;
const cell = { padding: '8px 10px', borderBottom: '1px solid #F3F4F6', fontSize: 13, verticalAlign: 'top' } as const;
const head = { ...cell, fontWeight: 600, color: '#6B7280', textAlign: 'left', borderBottom: '1px solid #E5E7EB' } as const;
const input = { padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, width: 'auto', maxWidth: '100%' } as const;
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const when = (s: string) => new Date(s).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

function StatusBadge({ status }: { status: Status }) {
  const [bg, fg] = STATUS_COLOUR[status];
  return <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '2px 8px', background: bg, color: fg }}>{STATUS_LABEL[status]}</span>;
}

export default function EnquiriesInboxScreen({ onBack, onConvert }: {
  onBack: () => void;
  /** Opens the Provisioning Wizard pre-filled from this enquiry. */
  onConvert: (e: EnquirySummary) => void;
}) {
  const [filters, setFilters] = useState({ status: 'open', interest: '', segment: '', owner: '', q: '' });
  const [rows, setRows] = useState<EnquirySummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [error, setError] = useState('');

  const query = () => new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
  const load = () => platformApi.get<{ rows: EnquirySummary[]; total: number }>(`/enquiries?${query()}`)
    .then((r) => { setRows(r.rows); setTotal(r.total); }).catch((e) => setError(errText(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters.status, filters.interest, filters.segment, filters.owner]);
  useEffect(() => { platformApi.get<Operator[]>('/enquiries/owners').then(setOperators).catch(() => {}); }, []);

  const exportCsv = async () => {
    try {
      const res = await fetch(`/platform-api/enquiries/export.csv?${query()}`, { headers: platformAuthHeader() });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(errText(e)); }
  };

  if (openId) {
    return <EnquiryDetailView id={openId} operators={operators} onBack={() => { setOpenId(null); load(); }} onConvert={onConvert} />;
  }

  const sel = (k: keyof typeof filters, options: [string, string][]) => (
    <select style={input} value={filters[k]} onChange={(e) => setFilters((f) => ({ ...f, [k]: e.target.value }))}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 16px' }}>
      <button onClick={onBack} style={btn}>← Back</button>
      <h1 style={{ fontSize: 20, margin: '14px 0 4px' }}>📨 Enquiries</h1>
      <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px', maxWidth: 820 }}>
        From the website&apos;s &ldquo;Talk to us&rdquo; form. Personal details are removed automatically 24 months after an enquiry
        (the Privacy Policy promise); the counts stay.
      </p>
      {error && <div role="alert" style={{ background: '#FEF2F2', color: '#991B1B', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        {sel('status', [['open', 'Open (new, contacted, demo)'], ['', 'All'], ...Object.entries(STATUS_LABEL)])}
        {sel('interest', [['', 'Any interest'], ...Object.entries(INTEREST_LABEL)])}
        {sel('segment', [['', 'Any type'], ...Object.entries(SEGMENT_LABEL)])}
        {sel('owner', [['', 'Any owner'], ['none', 'No owner'], ...operators.map((o) => [String(o.id), o.full_name || o.username] as [string, string])])}
        <form onSubmit={(e) => { e.preventDefault(); load(); }} style={{ display: 'flex', gap: 6 }}>
          <input style={input} placeholder="Search name, clinic, email, phone, ref" value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
          <button style={btn} type="submit">Search</button>
        </form>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: '#6B7280' }}>{total} enquir{total === 1 ? 'y' : 'ies'}</span>
        <button style={btn} onClick={exportCsv}>⬇ Export CSV</button>
      </div>
      {!rows ? <div style={{ fontSize: 13, color: '#6B7280' }}>Loading…</div> : rows.length === 0
        ? <div style={{ fontSize: 13, color: '#6B7280', padding: 24, textAlign: 'center' }}>No enquiries match.</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={head}>Received</th><th style={head}>Ref</th><th style={head}>Clinic / hospital</th><th style={head}>Contact</th>
              <th style={head}>Type</th><th style={head}>Interest</th><th style={head}>Owner</th><th style={head}>Status</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setOpenId(r.id)} style={{ cursor: 'pointer' }}>
                  <td style={cell}>{when(r.created_at)}</td>
                  <td style={cell}>{r.ref}</td>
                  <td style={cell}><strong>{r.organisation || (r.purged_at ? 'Removed (24 months)' : '—')}</strong>
                    {r.city && <div style={{ color: '#6B7280' }}>{r.city}{r.state ? `, ${r.state}` : ''}</div>}</td>
                  <td style={cell}>{r.name || '—'}<div style={{ color: '#6B7280' }}>{r.phone || ''}</div></td>
                  <td style={cell}>{r.segment ? SEGMENT_LABEL[r.segment] : '—'}</td>
                  <td style={cell}>{INTEREST_LABEL[r.interest] || r.interest}{r.plan_key ? ` (${r.plan_key})` : ''}</td>
                  <td style={cell}>{r.owner_name || '—'}</td>
                  <td style={cell}><StatusBadge status={r.status} />{r.tenant_slug && <div style={{ color: '#6B7280', marginTop: 4 }}>→ {r.tenant_slug}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function describeEvent(ev: EnquiryEvent, operators: Operator[]) {
  const opName = (id: unknown) => { const o = operators.find((x) => x.id === id); return o ? (o.full_name || o.username) : 'nobody'; };
  switch (ev.kind) {
    case 'received': return 'Enquiry received from the website';
    case 'duplicate': return 'Sent the form again within 24 hours';
    case 'status': return `Status: ${STATUS_LABEL[ev.before?.status as Status] || ev.before?.status} → ${STATUS_LABEL[ev.after?.status as Status] || ev.after?.status}`;
    case 'owner': return `Owner: ${opName(ev.before?.owner_operator_id)} → ${opName(ev.after?.owner_operator_id)}`;
    case 'link': return `Linked to hospital ${String(ev.after?.slug || '')} — marked Won`;
    default: return ev.body === null ? 'Note (removed after 24 months)' : ev.body;
  }
}

function EnquiryDetailView({ id, operators, onBack, onConvert }: {
  id: number; operators: Operator[]; onBack: () => void; onConvert: (e: EnquirySummary) => void;
}) {
  const [d, setD] = useState<EnquiryDetail | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [linkTo, setLinkTo] = useState('');

  useEffect(() => { platformApi.get<EnquiryDetail>(`/enquiries/${id}`).then(setD).catch((e) => setError(errText(e))); }, [id]);

  const act = async (fn: () => Promise<EnquiryDetail>) => {
    setBusy(true); setError('');
    try { setD(await fn()); } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };
  const patch = (body: Record<string, unknown>) => act(() => platformApi.patch<EnquiryDetail>(`/enquiries/${id}`, body));
  const addNote = () => act(async () => { const r = await platformApi.post<EnquiryDetail>(`/enquiries/${id}/notes`, { body: note }); setNote(''); return r; });
  const loadTenants = () => platformApi.get<TenantRow[]>('/tenants').then((t) => setTenants(t.filter((x) => x.status !== 'deleted'))).catch((e) => setError(errText(e)));
  const link = () => act(() => platformApi.post<EnquiryDetail>(`/enquiries/${id}/link-tenant`, { tenant_id: Number(linkTo) }));

  if (!d) return <div style={{ padding: 24, fontSize: 13, color: '#6B7280' }}>{error || 'Loading…'}</div>;
  const field = (label: string, value: React.ReactNode) => (
    <div style={{ marginBottom: 8 }}><div style={{ fontSize: 12, color: '#6B7280' }}>{label}</div><div style={{ fontSize: 14 }}>{value || '—'}</div></div>
  );

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
      <button onClick={onBack} style={btn}>← Back to enquiries</button>
      <h1 style={{ fontSize: 20, margin: '14px 0 4px' }}>{d.organisation || 'Personal details removed'} <span style={{ fontSize: 14, color: '#6B7280' }}>{d.ref}</span></h1>
      <div style={{ marginBottom: 16 }}><StatusBadge status={d.status} /> <span style={{ fontSize: 13, color: '#6B7280' }}>received {when(d.created_at)}</span></div>
      {error && <div role="alert" style={{ background: '#FEF2F2', color: '#991B1B', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {d.purged_at && <div style={{ background: '#F3F4F6', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>Personal details were removed on {when(d.purged_at)}, 24 months after the enquiry.</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
        <div>
          {field('Name', d.name)}{field('Role', d.role)}
          {field('Email', d.email && <a href={`mailto:${d.email}`}>{d.email}</a>)}
          {field('Phone', d.phone && <a href={`tel:${d.phone}`}>{d.phone}</a>)}
          {field('Place', [d.city, d.state].filter(Boolean).join(', '))}
          {field('Type', d.segment && SEGMENT_LABEL[d.segment])}
          {field('Doctors / beds', [d.doctors != null ? `${d.doctors} doctors` : '', d.beds != null ? `${d.beds} beds` : ''].filter(Boolean).join(', '))}
          {field('Interested in', `${INTEREST_LABEL[d.interest] || d.interest}${d.plan_key ? ` — plan ${d.plan_key}` : ''}`)}
          {field('Message', d.message && <span style={{ whiteSpace: 'pre-wrap' }}>{d.message}</span>)}
          {field('Sent from', [d.source_page, ...Object.entries(d.utm || {}).map(([k, v]) => `${k}=${v}`)].filter(Boolean).join(' · '))}
          {field('Consent', `Agreed to be contacted (${d.consent_text_version}) on ${when(d.consent_at)}`)}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#6B7280' }}>Status
            <select style={{ ...input, display: 'block', marginTop: 4 }} value={d.status} disabled={busy} onChange={(e) => patch({ status: e.target.value })}>
              {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginTop: 10 }}>Owner
            <select style={{ ...input, display: 'block', marginTop: 4 }} value={d.owner_operator_id ?? ''} disabled={busy}
              onChange={(e) => patch({ owner_operator_id: e.target.value ? Number(e.target.value) : null })}>
              <option value="">Nobody</option>
              {operators.map((o) => <option key={o.id} value={o.id}>{o.full_name || o.username}</option>)}
            </select></label>

          <div style={{ marginTop: 16, padding: 12, border: '1px solid #E5E7EB', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Hospital</div>
            {d.tenant_id ? <div style={{ fontSize: 13 }}>Linked to <strong>{d.tenant_name}</strong> ({d.tenant_slug}).</div> : <>
              <button style={primary} disabled={busy || !!d.purged_at} onClick={() => onConvert(d)}>Convert to tenant…</button>
              <div style={{ fontSize: 12, color: '#6B7280', margin: '6px 0 10px' }}>Opens the Provisioning Wizard with this hospital&apos;s name and plan filled in. When it finishes, this enquiry is marked Won and linked.</div>
              {tenants === null
                ? <button style={btn} onClick={loadTenants}>Link to an existing hospital…</button>
                : <div style={{ display: 'flex', gap: 6 }}>
                    <select style={input} value={linkTo} onChange={(e) => setLinkTo(e.target.value)}>
                      <option value="">Choose a hospital</option>
                      {tenants.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
                    </select>
                    <button style={btn} disabled={!linkTo || busy} onClick={link}>Link</button>
                  </div>}
            </>}
          </div>
        </div>
      </div>

      <h2 style={{ fontSize: 16, margin: '24px 0 8px' }}>History</h2>
      <form onSubmit={(e) => { e.preventDefault(); if (note.trim()) addNote(); }} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <textarea style={{ ...input, flex: 1, minHeight: 60 }} placeholder="Add a note (after a call, a demo …)" value={note} onChange={(e) => setNote(e.target.value)} />
        <button style={primary} type="submit" disabled={busy || !note.trim()}>Add note</button>
      </form>
      <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {[...d.events].reverse().map((ev) => (
          <li key={ev.id} style={{ borderLeft: '3px solid #E5E7EB', padding: '6px 12px', marginBottom: 6 }}>
            <div style={{ fontSize: 12, color: '#6B7280' }}>{when(ev.created_at)}{ev.operator_name ? ` · ${ev.operator_name}` : ''}</div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{describeEvent(ev, operators)}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}
